import { PDFDocument, StandardFonts, TextAlignment, rgb } from "pdf-lib";
import {
  buildAgentSummaryRows,
  currentYearRange,
  filterRowsForYear,
  mapEftRows,
} from "./payrollEftReportUtils";
import { paymentEftBelongsToAgent } from "./agentReportUtils";

const ZERO = "0.00";

/** Fallback when company-profile is missing (matches other ABC reports / EFT receipts). */
export const DEFAULT_COMPANY = {
  companyName: "ABC Realty Inc., Brokerage",
  address: "634 Champlain St",
  city: "Dieppe",
  province: "NB",
  postalCode: "E1A 1P4",
  hstNumber: "",
};

const SLIPS = [
  "form1[0].Page1[0].Slip1[0]",
  "form1[0].Page1[0].Slip2[0]",
];

const AMOUNT_SUFFIXES = [
  "Line16[0].Slip1Line16[0]",
  "Line18[0].Slip1Line18[0]",
  "Line20[0].Slip1Line20[0]",
  "Line22[0].Slip1Line22[0]",
  "Line24[0].Slip1Line24[0]",
  "Line48[0].Slip1Line48[0]",
];

function field(slip, suffix) {
  return `${slip}.${suffix}`;
}

function setText(form, name, value) {
  try {
    const f = form.getTextField(name);
    f.setText(value == null ? "" : String(value));
    return f;
  } catch (_) {
    return null;
  }
}

function setDropdown(form, name, value) {
  try {
    const f = form.getDropdown(name);
    const options = f.getOptions();
    const want = String(value);
    const match = options.find((o) => String(o) === want);
    if (match != null) f.select(match);
    return f;
  } catch (_) {
    return null;
  }
}

export function formatT4aAmount(n) {
  return (Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatSinDigits(sin) {
  return String(sin || "").replace(/\D/g, "").slice(0, 9);
}

function pickCompanyField(raw, key) {
  const fromRaw = String(raw?.[key] ?? "").trim();
  if (fromRaw) return fromRaw;
  return String(DEFAULT_COMPANY[key] ?? "").trim();
}

/**
 * Payer block for T4A (name + address), same layout as CRA samples:
 * Company name
 * Street
 * City, Province PostalCode
 */
export function buildCompanyPayerBlock(company) {
  const raw = company && typeof company === "object" ? company : {};
  const name = pickCompanyField(raw, "companyName");
  const address = pickCompanyField(raw, "address");
  const city = pickCompanyField(raw, "city");
  const province = pickCompanyField(raw, "province");
  const postalCode = pickCompanyField(raw, "postalCode");
  const cityLine = [city, province].filter(Boolean).join(", ");
  const cityPostal = [cityLine, postalCode].filter(Boolean).join(" ");
  return [name, address, cityPostal].filter(Boolean).join("\n");
}

export function buildAgentAddressBlock(agent) {
  if (!agent) return "";
  const street = [agent.streetNumber, agent.streetName, agent.unitNumber]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
  const cityLine = [agent.city, agent.province, agent.postalCode]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
  return [street, cityLine].filter(Boolean).join("\n");
}

export function resolveAgentRecipientNames(agent) {
  const legal = String(agent?.legalName || "").trim();
  if (legal) {
    return { firstName: "", initial: "", lastName: legal };
  }
  const firstName = String(agent?.firstName || "").trim();
  const lastName = String(agent?.lastName || "").trim();
  const middle = String(agent?.middleName || "").trim();
  const initial = middle ? middle.charAt(0).toUpperCase() : "";
  return { firstName, initial, lastName };
}

/** Build an appUser-shaped object from an Agent document for EFT matching. */
export function appUserFromAgent(agent) {
  if (!agent) return null;
  return {
    agentId: agent._id,
    employeeNo: agent.employeeNo,
    displayName: [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim(),
  };
}

/**
 * Box 20 amount = Agent Summary "Total Payment" (commission without HST)
 * for the year, not the HST-inclusive Net Commission.
 */
export function computeAgentYearNetCommission(efts, agents, appUser, yearRange) {
  let list = (efts || []).filter((e) => e.type === "AgentCommissionTransfer");
  if (appUser) {
    list = list.filter((e) => paymentEftBelongsToAgent(e, appUser));
  }
  const rows = filterRowsForYear(
    mapEftRows(list, agents),
    yearRange.fromIso,
    yearRange.toIso,
  );
  const summary = buildAgentSummaryRows(rows);
  const total = summary.reduce(
    (sum, row) => sum + (Number(row.totalPayment) || 0),
    0,
  );
  return Math.round(total * 100) / 100;
}

/** Box 20 Total Payment for a specific agent (admin T4A picker). */
export function computeNetCommissionForAgent(efts, agents, agent, yearRange) {
  return computeAgentYearNetCommission(
    efts,
    agents,
    appUserFromAgent(agent),
    yearRange,
  );
}

export function findLoggedInAgent(agents, appUser) {
  if (!appUser) return null;
  const byMongo = String(appUser.agentId || "").trim();
  const emp = String(appUser.employeeNo ?? "").trim();
  const name = String(appUser.displayName || "").trim().toLowerCase();
  return (
    (agents || []).find((agent) => {
      if (byMongo && String(agent._id) === byMongo) return true;
      if (emp && String(agent.employeeNo ?? "").trim() === emp) return true;
      const full = `${agent.firstName || ""} ${agent.lastName || ""}`
        .trim()
        .toLowerCase();
      return Boolean(name && full && full === name);
    }) || null
  );
}

function widgetRect(textField) {
  const widgets = textField?.acroField?.getWidgets?.() || [];
  if (!widgets.length) return null;
  return widgets[0].getRectangle();
}

function paintJobsOnPage(page, font, jobs) {
  for (const job of jobs) {
    const { rect, text, align = "left", size = 9, multiline = false } = job;
    if (!rect) continue;
    page.drawRectangle({
      x: rect.x + 0.4,
      y: rect.y + 0.4,
      width: Math.max(0, rect.width - 0.8),
      height: Math.max(0, rect.height - 0.8),
      color: rgb(1, 1, 1),
    });
    const value = String(text ?? "");
    if (!value) continue;

    if (multiline) {
      const lines = value
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      let y = rect.y + rect.height - size - 2;
      for (const line of lines) {
        page.drawText(line, {
          x: rect.x + 3,
          y,
          size,
          font,
          maxWidth: Math.max(8, rect.width - 6),
        });
        y -= size + 1.5;
        if (y < rect.y) break;
      }
      continue;
    }

    const tw = font.widthOfTextAtSize(value, size);
    let x = rect.x + 3;
    if (align === "right") x = rect.x + rect.width - tw - 4;
    if (align === "center") x = rect.x + (rect.width - tw) / 2;
    const y = rect.y + Math.max(1.5, (rect.height - size) / 2);
    page.drawText(value, { x, y, size, font });
  }
}

/**
 * Fill CRA T4A template for one agent. Fills both slips on the form.
 */
export async function fillT4aPdfBytes(templateBytes, payload) {
  const {
    year,
    companyBlock,
    payerAccount = "",
    agent,
    netCommission,
  } = payload;

  const pdf = await PDFDocument.load(templateBytes, {
    ignoreEncryption: true,
    updateMetadata: false,
  });
  const form = pdf.getForm();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const page = pdf.getPages()[0];

  const names = resolveAgentRecipientNames(agent);
  const address = buildAgentAddressBlock(agent);
  const sin = formatSinDigits(agent?.sin);
  const box13 = String(agent?.hstNumber || "").trim();
  const box20 = formatT4aAmount(netCommission);
  const yearStr = String(year || currentYearRange().year);
  // Always include company name + address on both slips.
  const payerBlock =
    String(companyBlock || "").trim() ||
    buildCompanyPayerBlock(DEFAULT_COMPANY);

  const amountValues = {
    "Line16[0].Slip1Line16[0]": ZERO,
    "Line18[0].Slip1Line18[0]": ZERO,
    "Line20[0].Slip1Line20[0]": box20,
    "Line22[0].Slip1Line22[0]": ZERO,
    "Line24[0].Slip1Line24[0]": ZERO,
    "Line48[0].Slip1Line48[0]": ZERO,
  };

  /** @type {{rect: any, text: string, align?: string, size?: number, multiline?: boolean}[]} */
  const overlayJobs = [];

  function queueOverlay(tf, text, opts) {
    if (!tf || text == null || text === "") return;
    const rect = widgetRect(tf);
    if (!rect) return;
    overlayJobs.push({ rect, text: String(text), ...opts });
  }

  // Fill Slip1 (top) and Slip2 (bottom) with the same data.
  for (const slip of SLIPS) {
    const employersName = setText(
      form,
      field(slip, "EmployersName[0].Slip1EmployersName[0]"),
      payerBlock,
    );
    if (employersName) {
      try {
        employersName.enableMultiline();
      } catch (_) {}
      queueOverlay(employersName, payerBlock, { multiline: true, size: 7.5 });
    }

    const yearField = setText(
      form,
      field(slip, "Year[0].Slip1Year[0]"),
      yearStr,
    );
    queueOverlay(yearField, yearStr, { align: "center", size: 11 });

    const accountField = setText(
      form,
      field(slip, "EmployersAccount[0].Slip1EmployersAccount[0]"),
      payerAccount,
    );
    queueOverlay(accountField, payerAccount, { size: 8 });

    const sinField = setText(form, field(slip, "Box12[0].Slip1SIN[0]"), sin);
    queueOverlay(sinField, sin, { size: 9 });

    const box13Field = setText(
      form,
      field(slip, "Box13[0].Slip1Box13[0]"),
      box13,
    );
    queueOverlay(box13Field, box13, { size: 8 });

    setDropdown(form, field(slip, "Slip1Line15[0]"), "1");

    for (const suffix of AMOUNT_SUFFIXES) {
      const tf = setText(form, field(slip, suffix), amountValues[suffix]);
      if (!tf) continue;
      try {
        tf.setAlignment(TextAlignment.Right);
      } catch (_) {}
      queueOverlay(tf, amountValues[suffix], { align: "right", size: 9 });
    }

    const firstField = setText(
      form,
      field(slip, "Employee[0].FirstName[0].Slip1FirstName[0]"),
      names.firstName,
    );
    queueOverlay(firstField, names.firstName, { size: 9 });

    const initialField = setText(
      form,
      field(slip, "Employee[0].Initial[0].Slip1Initial[0]"),
      names.initial,
    );
    queueOverlay(initialField, names.initial, { size: 9 });

    const lastField = setText(
      form,
      field(slip, "Employee[0].LastName[0].Slip1LastName[0]"),
      names.lastName,
    );
    queueOverlay(lastField, names.lastName, { size: 9 });

    const addrField = setText(
      form,
      field(slip, "Employee[0].Slip1Address[0]"),
      address,
    );
    if (addrField) {
      try {
        addrField.enableMultiline();
      } catch (_) {}
      queueOverlay(addrField, address, { multiline: true, size: 7.5 });
    }
  }

  try {
    form.updateFieldAppearances(font);
  } catch (_) {}

  try {
    form.flatten();
  } catch (_) {}

  // Paint on top for both slips so company address + amounts stay aligned.
  paintJobsOnPage(page, font, overlayJobs);

  return pdf.save();
}

export async function loadT4aTemplateBytes() {
  const res = await fetch(`${process.env.PUBLIC_URL || ""}/t4a-fill-25e.pdf`);
  if (!res.ok) {
    throw new Error("Could not load T4A PDF template.");
  }
  return new Uint8Array(await res.arrayBuffer());
}

/** Merge multiple filled T4A PDFs into one document (one agent per page set). */
export async function mergeT4aPdfBytes(pdfBytesList) {
  const list = (pdfBytesList || []).filter(Boolean);
  if (!list.length) {
    throw new Error("No T4A PDFs to merge.");
  }
  if (list.length === 1) return list[0];

  const merged = await PDFDocument.create();
  for (const bytes of list) {
    const doc = await PDFDocument.load(bytes, {
      ignoreEncryption: true,
      updateMetadata: false,
    });
    const pages = await merged.copyPages(doc, doc.getPageIndices());
    for (const page of pages) merged.addPage(page);
  }
  return merged.save();
}
